import React, { useState, useEffect, useCallback } from 'react';
import { Container, Table, Form, Button, Spinner, InputGroup, Badge, Card, Row, Col, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import api from '../../config/api';
import './AdminPanel.css';

const StockManager = () => {
  const [phoneModels, setPhoneModels] = useState([]);
  const [filteredPhoneModels, setFilteredPhoneModels] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingStocks, setSavingStocks] = useState({});
  const [selectedBrand, setSelectedBrand] = useState('all');
  const [brandList, setBrandList] = useState([]);
  const [expandedModels, setExpandedModels] = useState({});
  
  // Ürün tipi filtreleme için state'ler
  const [productTypes, setProductTypes] = useState([]);
  const [selectedProductType, setSelectedProductType] = useState('');
  const [productTypeFilteredModels, setProductTypeFilteredModels] = useState({});

  // Ürün tiplerini yükle
  const fetchProductTypes = useCallback(async () => {
    try {
      const response = await api.get('/admin/product-types');
      setProductTypes(response.data);
    } catch (error) {
      console.error('Ürün tipleri yüklenirken hata:', error);
      toast.error('Ürün tipleri yüklenemedi!');
    }
  }, []);

  // Telefon modellerini ve ilgili renkleri yükle
  const fetchPhoneModels = useCallback(async (productTypeId = null) => {
    try {
      setLoading(true);
      
      // Model-renk ilişkilerini getir (ürün tipine göre filtrelenmiş)
      let url = '/admin/phone-model-colors';
      if (productTypeId) {
        url += `?product_type_id=${productTypeId}`;
      }
      
      const response = await api.get(url);
      const relationData = response.data;
      
      // Önce tüm telefon modellerini grupla
      const modelsMap = {};
      relationData.forEach(relation => {
        if (!relation.phone_models) return;
        
        const modelId = relation.phone_model_id;
        const modelInfo = relation.phone_models;
        
        if (!modelsMap[modelId]) {
          modelsMap[modelId] = {
            id: modelId,
            brand: modelInfo.brand,
            model_name: modelInfo.model_name,
            colors: []
          };
        }
        
        // Renk bilgisini ekle
        if (relation.product_colors) {
          modelsMap[modelId].colors.push({
            id: relation.product_colors.id,
            name: relation.product_colors.name,
            code: relation.product_colors.code,
            stock: relation.stock || 0,
            relation_id: relation.id // İlişki ID'si
          });
        }
      });
      
      // Modelleri array'e dönüştür
      const models = Object.values(modelsMap);
      
      // Marka listesini oluştur
      const brands = [...new Set(models.map(model => model.brand))].sort();
      setBrandList(brands);
      
      // Marka bazında gruplanmış telefon modellerini hazırla
      const groupedModels = models.reduce((acc, model) => {
        const brand = model.brand || 'Diğer';
        if (!acc[brand]) {
          acc[brand] = [];
        }
        acc[brand].push(model);
        return acc;
      }, {});
      
      // Her marka için telefon modellerini model adına göre sırala
      Object.keys(groupedModels).forEach(brand => {
        groupedModels[brand].sort((a, b) => 
          a.model_name.localeCompare(b.model_name)
        );
      });
      
      setPhoneModels(models);
      setFilteredPhoneModels(groupedModels);
      setLoading(false);
    } catch (error) {
      console.error('Telefon modelleri yüklenirken hata:', error);
      toast.error('Telefon modelleri yüklenemedi!');
      setLoading(false);
    }
  }, []);

  // İlk yükleme
  useEffect(() => {
    fetchProductTypes();
    fetchPhoneModels();
  }, [fetchProductTypes, fetchPhoneModels]);

  // Ürün tipi değiştiğinde telefon modellerini yeniden yükle
  useEffect(() => {
    if (selectedProductType) {
      fetchPhoneModels(selectedProductType);
    } else {
      fetchPhoneModels();
    }
  }, [selectedProductType, fetchPhoneModels]);

  // Arama ve filtreleme işlemi
  useEffect(() => {
    // Tüm modeller
    const allModels = phoneModels;
    
    // Arama ve marka filtreleme
    const filtered = allModels.filter(model => {
      const matchesSearch = searchTerm === '' || 
                           model.brand.toLowerCase().includes(searchTerm.toLowerCase()) || 
                           model.model_name.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesBrand = selectedBrand === 'all' || model.brand === selectedBrand;
      
      return matchesSearch && matchesBrand;
    });
    
    // Gruplanmış modeller
    const grouped = filtered.reduce((acc, model) => {
      const brand = model.brand || 'Diğer';
      if (!acc[brand]) {
        acc[brand] = [];
      }
      acc[brand].push(model);
      return acc;
    }, {});
    
    // Her marka için sıralama
    Object.keys(grouped).forEach(brand => {
      grouped[brand].sort((a, b) => 
        a.model_name.localeCompare(b.model_name)
      );
    });
    
    setFilteredPhoneModels(grouped);
  }, [phoneModels, searchTerm, selectedBrand]);

  // Stok miktarını güncelle
  const handleStockChange = (relationId, newValue) => {
    const value = parseInt(newValue, 10);
    
    // Renk bilgisini güncelle
    setPhoneModels(prevModels => {
      return prevModels.map(model => {
        const updatedColors = model.colors?.map(color => {
          if (color.relation_id === relationId) {
            return { ...color, stock: isNaN(value) ? 0 : value };
          }
          return color;
        });
        
        return {
          ...model,
          colors: updatedColors
        };
      });
    });
  };

  // Stok değişikliklerini kaydet
  const saveStockChanges = async (modelId, relationId, newStock) => {
    try {
      setSavingStocks(prev => ({ ...prev, [relationId]: true }));
      
      // API'ye stok güncellemesi gönder
      await api.put(`/admin/phone-model-colors/${relationId}/stock`, {
        stock: newStock
      });
      
      toast.success('Stok güncellendi!');
    } catch (error) {
      console.error('Stok güncellenirken hata:', error);
      toast.error('Stok güncellenemedi!');
      
      // Hata durumunda telefon modellerini yeniden yükle
      fetchPhoneModels();
    } finally {
      setSavingStocks(prev => ({ ...prev, [relationId]: false }));
    }
  };

  // Model görünümünü genişlet/daralt
  const toggleModelExpand = (modelId) => {
    setExpandedModels(prev => ({
      ...prev,
      [modelId]: !prev[modelId]
    }));
  };

  return (
    <Container className="admin-panel stock-manager py-4">
      <h2>Stok Yönetimi</h2>
      <p className="text-muted mb-4">
        Bu sayfada telefon modelleri ve renkleri için stok miktarlarını görüntüleyebilir ve güncelleyebilirsiniz.
      </p>
      
      {/* Ürün Tipi Seçici */}
      <div className="product-type-selector mb-4 p-3 border rounded bg-light">
        <h5 className="mb-3">
          <span className="me-2">📱</span>
          Ürün Tipi Seçin
        </h5>
        <div className="d-flex flex-wrap gap-3 align-items-center">
          <Form.Group style={{ minWidth: '250px' }}>
            <Form.Select 
              value={selectedProductType} 
              onChange={(e) => setSelectedProductType(e.target.value)}
              className="form-select-lg"
            >
              <option value="">Tüm Ürün Tipleri</option>
              {productTypes.map(type => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          
          {selectedProductType && (
            <Badge bg="primary" className="fs-6 px-3 py-2">
              <span className="me-2">✅</span>
              {productTypes.find(t => t.id == selectedProductType)?.name}
            </Badge>
          )}
        </div>
      </div>

      {/* Arama ve Filtreleme */}
      <div className="d-flex flex-wrap gap-3 mb-4">
        <Form.Group className="flex-grow-1" style={{ maxWidth: '400px' }}>
          <InputGroup>
            <InputGroup.Text>
              <i className="fa fa-search"></i>
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Model ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
        </Form.Group>
        
        <Form.Group style={{ minWidth: '200px' }}>
          <Form.Select 
            value={selectedBrand} 
            onChange={(e) => setSelectedBrand(e.target.value)}
          >
            <option value="all">Tüm Markalar</option>
            {brandList.map(brand => (
              <option key={brand} value={brand}>
                {brand}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        
        <Button 
          variant="outline-primary" 
          onClick={fetchPhoneModels}
          disabled={loading}
          className="ms-auto"
        >
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Yükleniyor
            </>
          ) : (
            <>
              <i className="fa fa-refresh me-2"></i>
              Yenile
            </>
          )}
        </Button>
      </div>
      
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
          <p className="mt-3">Stok bilgileri yükleniyor...</p>
        </div>
      ) : Object.keys(filteredPhoneModels).length === 0 ? (
        <Alert variant="info">
          {selectedProductType ? (
            <>
              <strong>Seçili ürün tipi için telefon modeli bulunamadı.</strong><br />
              Bu ürün tipinde henüz telefon modeli ve renk kombinasyonu eklenmemiş olabilir.
            </>
          ) : (
            <>
              <strong>Arama kriterlerinize uygun telefon modeli bulunamadı.</strong><br />
              Ürün tipi seçerek daha spesifik sonuçlar elde edebilirsiniz.
            </>
          )}
        </Alert>
      ) : (
        // Marka bazında gruplandırılmış modeller
        Object.entries(filteredPhoneModels).map(([brand, models]) => (
          <Card key={brand} className="mb-4 shadow-sm">
            <Card.Header className="bg-light">
              <h5 className="mb-0">{brand}</h5>
            </Card.Header>
            <Card.Body className="p-0">
              <Table responsive hover className="mb-0">
                <thead>
                  <tr>
                    <th style={{ width: '40%' }}>Model</th>
                    <th style={{ width: '60%' }}>Renk ve Stok Bilgisi</th>
                  </tr>
                </thead>
                <tbody>
                  {models.map(model => (
                    <tr key={model.id}>
                      <td className="align-middle">
                        <div 
                          className="d-flex align-items-center"
                          style={{ cursor: 'pointer' }}
                          onClick={() => toggleModelExpand(model.id)}
                        >
                          <span className="me-2">
                            {expandedModels[model.id] ? (
                              <i className="fa fa-chevron-down"></i>
                            ) : (
                              <i className="fa fa-chevron-right"></i>
                            )}
                          </span>
                          <div>
                            <div>{model.model_name}</div>
                            <small className="text-muted">
                              {model.colors?.length || 0} renk seçeneği
                            </small>
                          </div>
                        </div>
                      </td>
                      <td>
                        {expandedModels[model.id] ? (
                          <div className="py-2">
                            {model.colors && model.colors.length > 0 ? (
                              <Row className="g-3">
                                {model.colors.map(color => (
                                  <Col md={6} lg={4} xl={3} key={color.id}>
                                    <div className="border rounded p-2 h-100">
                                      <div className="d-flex align-items-center mb-2">
                                        <div 
                                          className="color-preview me-2" 
                                          style={{ 
                                            backgroundColor: color.code && color.code.startsWith('#') ? color.code : `#${color.code}`,
                                            width: '24px',
                                            height: '24px',
                                            borderRadius: '4px',
                                            border: '1px solid #ddd'
                                          }}
                                        ></div>
                                        <div className="flex-grow-1 text-truncate" title={color.name}>
                                          {color.name}
                                        </div>
                                      </div>
                                      <InputGroup size="sm">
                                        <Form.Control
                                          type="number"
                                          min="0"
                                          value={color.stock || 0}
                                          onChange={(e) => handleStockChange(color.relation_id, e.target.value)}
                                          aria-label="Stok miktarı"
                                        />
                                        <Button
                                          variant="primary"
                                          onClick={() => saveStockChanges(model.id, color.relation_id, color.stock || 0)}
                                          disabled={savingStocks[color.relation_id]}
                                        >
                                          {savingStocks[color.relation_id] ? (
                                            <Spinner animation="border" size="sm" />
                                          ) : (
                                            <i className="fa fa-check"></i>
                                          )}
                                        </Button>
                                      </InputGroup>
                                    </div>
                                  </Col>
                                ))}
                              </Row>
                            ) : (
                              <Alert variant="warning" className="mb-0">
                                Bu model için renk seçeneği bulunmuyor.
                              </Alert>
                            )}
                          </div>
                        ) : (
                          <div className="d-flex flex-wrap gap-2">
                            {model.colors && model.colors.slice(0, 3).map(color => (
                              <Badge 
                                key={color.id} 
                                bg={color.stock > 0 ? "success" : "danger"}
                                className="d-flex align-items-center"
                              >
                                <div 
                                  style={{ 
                                    width: '10px', 
                                    height: '10px', 
                                    backgroundColor: color.code && color.code.startsWith('#') ? color.code : `#${color.code}`,
                                    borderRadius: '50%',
                                    marginRight: '5px',
                                    border: '1px solid rgba(255,255,255,0.5)'
                                  }}
                                ></div>
                                {color.name}: {color.stock || 0}
                              </Badge>
                            ))}
                            {model.colors && model.colors.length > 3 && (
                              <Badge bg="secondary">
                                +{model.colors.length - 3} daha
                              </Badge>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        ))
      )}
    </Container>
  );
};

export default StockManager; 